import { useCallback } from 'react'

import { type CreatorStatistic, SalaryRetention } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import type { DialogProps } from '@/dialogs/types'
import { useSetCompensation } from '@/modules/accounting/hooks/useSetCompensation.ts'
import { getSalary } from '@/modules/accounting/utils.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { roundAmount } from '@/utils/number.ts'

import { CreatorSalaryForm } from './CreatorSalaryForm.tsx'
import { CreatorSalarySchema, type CreatorSalaryType } from './validations.ts'

interface Props extends DialogProps {
  formattedRange?: string
  data: CreatorStatistic
  showCents: boolean
}

export const SetCreatorSalaryDialog = ({
  isOpen,
  onClose,
  formattedRange,
  data,
  showCents,
}: Props) => {
  const { updateRevenue, isLoading: isRevenueLoading } = useSetCompensation({
    type: 'creator',
  })

  const form = useForm<CreatorSalaryType>({
    defaultValues: {
      fixedSalary: {
        amount: data.compensation?.fixedSalary ?? 0,
        retention: data.compensation?.salaryRetention ?? SalaryRetention.YEARLY,
      },
      cutOfRevenuePercentage: data.compensation?.cutOfRevenuePercentage ?? 0,
    },
    resolver: zodResolver(CreatorSalarySchema),
  })

  const onSubmit = useCallback(
    async (values: CreatorSalaryType) => {
      if (values) {
        await updateRevenue(data.creatorId!, values)
        onClose()
      }
    },
    [data, onClose, updateRevenue],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      testId="set-salary-modal"
    >
      <Dialog.Header>
        <Dialog.Title>Set Creator Salary</Dialog.Title>
      </Dialog.Header>

      <div className="mt-4">
        <div>
          <div className="text-text-default mb-2 text-sm font-medium">
            Selected Creator
          </div>
          <div className="border-border-secondary bg-secondary-50 dark:bg-secondary-600 flex items-center rounded-md border px-3 py-2">
            <CreatorAvatar
              className="mr-2"
              name={data.creatorName!}
              avatarSrc={data.avatar!}
              size={24}
            />
            <div className="text-text-default">{data.creatorName}</div>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <p className="text-text-default font-medium">Revenue</p>
          <p className="text-secondary-300 text-sm">({formattedRange})</p>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-text-default text-sm">Total Revenue</p>
          <p className="text-text-default text-sm font-medium">
            {roundAmount(data?.revenue, showCents)}
          </p>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-text-default text-sm">Creator Salary</p>
          <p className="text-text-default text-sm font-medium">
            {roundAmount(getSalary(data), showCents)}
          </p>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-text-default text-sm">My Cut</p>
          <p className="text-text-default text-sm font-medium">
            {roundAmount(data?.agencyProfit, showCents)}
          </p>
        </div>
      </div>

      <Separator className="my-5" />
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CreatorSalaryForm disabled={isRevenueLoading} />
          <Dialog.Footer className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              isLoading={isRevenueLoading}
              type="submit"
              testId="set-salary-submit-button"
            >
              Save
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
